import React, { useState, useCallback, useEffect, useRef } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { steps } from "../../../../data/DataFile";
import HeaderNew from "../../../../common/components/custom/header-new/HeaderNew";
import HubHeader from "../../../../common/components/custom/hub-header/HubHeader";
import SidebarConfigureOps from "../sidebar-configure-ops/SidebarConfigureOps";
import { useGetConfigurationOperationsQuery } from "../../data/api/queries/assignments";
import {
  DocumentTypeConfigService,
  ConfigOption
} from "../../data/services/document-type-config.service";
import {
  FlowConfigService,
  CreateFlowConfigRequest
} from "../../data/services/flow-config.service";
import {
  TagLibraryService,
  TagLibraryResponse
} from "../../data/services/tag-library.service";
import useProfile from "../../../../common/hooks/useProfile";
import { ToastMessageUtil } from "../../../../common/components/base/toast-message/ToastMessage.util";
import { ToastVariant } from "../../../../common/enums/controls/toast.enum";
import "./DocumentOperation.scss";
import InfoIcon from "@mui/icons-material/Info";
import { useAppDispatch, useAppSelector } from "../../../../common/redux/core";
import { setSelectedDocumentType } from "../../../_shared/redux/document-type";

const DocumentOperation: React.FC = () => {
  const [selectedFlowConfig, setSelectedFlowConfig] = useState<string>("");
  const [selectedIngestionFrom, setSelectedIngestionFrom] =
    useState<string>("");
  const [sharepointUrl, setSharepointUrl] = useState<string>("");
  // Use ref to track the current ingestion value to avoid stale closure issues
  const selectedIngestionFromRef = useRef<string>("");

  const [selectedAttributesExtraction, setSelectedAttributesExtraction] =
    useState<number | null>();
  const [selectedDocumentTagging, setSelectedDocumentTagging] =
    useState<string>("");
  const [selectedSimilaritySearch, setSelectedSimilaritySearch] =
    useState<string>("");
  const [selectedExport, setSelectedExport] = useState<string>("");
  const [selectedTagConfiguration, setSelectedTagConfiguration] =
    useState<string>("");
  const [selectedOperations, setSelectedOperations] = useState<string[]>([]);
  const [isDocOperationsOpen, setIsDocOperationsOpen] = useState<boolean>(true);
  const [showIngestionFrom, setShowIngestionFrom] = useState<boolean>(false);
  const [showAttributesExtraction, setShowAttributesExtraction] =
    useState<boolean>(false);
  const [showDocumentTagging, setShowDocumentTagging] =
    useState<boolean>(false);
  const [showSimilaritySearch, setShowSimilaritySearch] =
    useState<boolean>(false);
  const [showTagConfigurations, setShowTagConfigurations] =
    useState<boolean>(false);
  const [showExport, setShowExport] = useState<boolean>(false);

  // State for dynamic attribute extraction options
  const [attributeExtractionOptions, setAttributeExtractionOptions] = useState<
    ConfigOption[]
  >([]);
  const [isLoadingOptions, setIsLoadingOptions] = useState<boolean>(false);
  const [optionsError, setOptionsError] = useState<string | null>(null);

  // State for tag configuration options
  const [tagConfigurationOptions, setTagConfigurationOptions] = useState<
    TagLibraryResponse[]
  >([]);
  const [isLoadingTagOptions, setIsLoadingTagOptions] =
    useState<boolean>(false);
  const [tagOptionsError, setTagOptionsError] = useState<string | null>(null);

  // State for saving configuration
  const [isSaving, setIsSaving] = useState<boolean>(false);
  const [saveError, setSaveError] = useState<string | null>(null);
  const [additionalFlowConfigs, setAdditionalFlowConfigs] = useState<
    Array<{
      id: number;
      selectedFlow: string;
      showIngestion: boolean;
      showAttributes: boolean;
      showTagging: boolean;
      showSimilarity: boolean;
      showTagConfigurations: boolean;
      showExport: boolean;
      selectedIngestion: string;
      selectedAttributes: string;
      sharepointUrl: string;
      selectedTagging: string;
      selectedSimilarityValue: string;
      selectedTagConfigurationValue: string;
      selectedExportValue: string;
    }>
  >([]);
  const [selectedFlowConfigs, setSelectedFlowConfigs] = useState<string[]>([]);

  const navigate = useNavigate();
  const location = useLocation();
  const dispatch = useAppDispatch();

  // Get global Redux state for document type
  const { selectedDocumentType } = useAppSelector(
    (state) => state.documentType
  );

  // Get current user profile
  const { displayName, mail, userPrincipalName } = useProfile();

  // Use userPrincipalName as it contains the email (mail is null)
  const userEmail = userPrincipalName || displayName || "";

  // Get assignment ID from URL params if available
  const urlParams = new URLSearchParams(location.search);
  const assignmentId = urlParams.get("assignmentId");
  const flowNameFromUrl = urlParams.get("flowName");
  const flowDescriptionFromUrl = urlParams.get("flowDescription");
  const documentTypeFromUrl = urlParams.get("documentType");

  // Edit mode detection
  const editMode = urlParams.get("editMode") === "true";

  // Get configuration parameters - use different handling for edit vs pre-selection
  const configIdFromUrl = editMode ? null : urlParams.get("configId"); // Only for pre-selection
  const editConfigId = editMode ? urlParams.get("configId") : null; // Only for edit mode
  const configNameFromUrl = urlParams.get("configName");

  // Decode URL parameters
  const flowName = flowNameFromUrl
    ? decodeURIComponent(flowNameFromUrl)
    : "New Flow Configuration";
  const flowDescription = flowDescriptionFromUrl
    ? decodeURIComponent(flowDescriptionFromUrl)
    : "Flow configuration description";

  // Get the current user for created_by field
  const currentUser = displayName || userPrincipalName || "Unknown User";

  // RTK Query to get existing configurations
  const {
    data: configurationsData,
    isLoading: isLoadingConfigs,
    isError: isConfigsError
  } = useGetConfigurationOperationsQuery(
    assignmentId ? Number(assignmentId) : undefined
  );

  const currentStep = 2;

  // Immediate pre-selection on component mount (backup mechanism)
  useEffect(() => {
    const immediateConfigId = !editMode ? urlParams.get("configId") : null;

    if (immediateConfigId && !editMode) {
      setSelectedFlowConfig("attributes-extraction");
      setShowAttributesExtraction(true);
      setSelectedOperations(["attributes-extraction"]);
      setSelectedFlowConfigs(["attributes-extraction"]);

      const configId = parseInt(immediateConfigId);
      if (!isNaN(configId)) {
        setSelectedAttributesExtraction(configId);
      }
    }
  }, []); // Empty dependency array - runs only once on mount

  // Sync document type from URL to Redux state
  useEffect(() => {
    if (documentTypeFromUrl) {
      const decodedDocumentType = decodeURIComponent(documentTypeFromUrl);
      if (decodedDocumentType !== selectedDocumentType) {
        dispatch(setSelectedDocumentType(decodedDocumentType));
      }
    }
  }, [documentTypeFromUrl, selectedDocumentType, dispatch]);

  // Fetch attribute extraction options
  const fetchAttributeExtractionOptions = useCallback(async () => {
    setIsLoadingOptions(true);
    setOptionsError(null);

    try {
      // Use the document type from URL parameter, default to "contract" if not provided
      const documentType = documentTypeFromUrl
        ? decodeURIComponent(documentTypeFromUrl)
        : "";

      // Map non-standard document types to "Others" for extraction configuration lookup
      const result =
        await DocumentTypeConfigService.getDocumentTypeConfigurations(
          documentType
        );

      if (!result.hasErrors && result.value) {
        const uniqueOptions = DocumentTypeConfigService.getUniqueConfigOptions(
          result.value
        );
        const tempList = uniqueOptions.filter(
          (option) => option.doc_type_id.toString() === documentType.toString()
        );
        setAttributeExtractionOptions(tempList);
      } else {
        const errorMessage =
          result.errors.length > 0
            ? result.errors.join(", ")
            : "Failed to fetch attribute extraction options";
        setOptionsError(errorMessage);
        console.error("Attribute Extraction Options API Error:", result.errors);
      }
    } catch (error) {
      const errorMessage =
        error instanceof Error ? error.message : "Unknown error occurred";
      setOptionsError(errorMessage);
      console.error("Error fetching attribute extraction options:", error);
    } finally {
      setIsLoadingOptions(false);
    }
  }, [documentTypeFromUrl]);

  // Fetch tag configuration options
  const fetchTagConfigurationOptions = useCallback(async () => {
    setIsLoadingTagOptions(true);
    setTagOptionsError(null);

    try {
      const result = await TagLibraryService.getAllTagLibraries();

      if (!result.hasErrors && result.value) {
        // result.value is already the tagLibrary array from the service
        const tagLibraryData = result.value;

        // Ensure the result is an array before setting
        if (Array.isArray(tagLibraryData)) {
          setTagConfigurationOptions(tagLibraryData);
        } else {
          console.error(
            "Tag Library API returned non-array data:",
            result.value
          );
          setTagConfigurationOptions([]);
          setTagOptionsError("Invalid data format received from server");
        }
      } else {
        const errorMessage =
          result.errors.length > 0
            ? result.errors.join(", ")
            : "Failed to fetch tag configurations";
        setTagOptionsError(errorMessage);
        console.error("Tag Library Options API Error:", result.errors);
        // Reset to empty array on error
        setTagConfigurationOptions([]);
      }
    } catch (error) {
      const errorMessage =
        error instanceof Error
          ? error.message
          : "Failed to fetch tag configurations";
      setTagOptionsError(errorMessage);
      console.error("Error fetching tag configuration options:", error);
      // Reset to empty array on error
      setTagConfigurationOptions([]);
    } finally {
      setIsLoadingTagOptions(false);
    }
  }, []);

  // Reset selectedAttributesExtraction when document type changes in edit mode
  // This forces user to manually select appropriate config for the new document type
  useEffect(() => {
    // Only reset in edit mode when document type changes, but not when coming from extraction config page
    if (editMode && documentTypeFromUrl && !configIdFromUrl) {
      setSelectedAttributesExtraction(null);
    }
  }, [documentTypeFromUrl, editMode, configIdFromUrl]);

  // Load options when component mounts or when attributes extraction is shown
  useEffect(() => {
    const hasAttributesExtraction =
      showAttributesExtraction ||
      additionalFlowConfigs.some((config) => config.showAttributes);

    if (hasAttributesExtraction && attributeExtractionOptions.length === 0) {
      fetchAttributeExtractionOptions();
    }
  }, [
    showAttributesExtraction,
    additionalFlowConfigs,
    attributeExtractionOptions.length,
    fetchAttributeExtractionOptions
  ]);

  // Load tag configuration options when tag configurations section is shown
  useEffect(() => {
    const hasTagConfigurations =
      showTagConfigurations ||
      additionalFlowConfigs.some((config) => config.showTagConfigurations);

    if (hasTagConfigurations && tagConfigurationOptions.length === 0) {
      fetchTagConfigurationOptions();
    }
  }, [
    showTagConfigurations,
    additionalFlowConfigs,
    tagConfigurationOptions.length,
    fetchTagConfigurationOptions
  ]);

  // Pre-select configuration when coming from extraction configuration page
  useEffect(() => {
    if (configIdFromUrl && !editMode) {
      // Force pre-select "attributes-extraction" in the main dropdown
      setSelectedFlowConfig("attributes-extraction");
      setShowAttributesExtraction(true);

      // Add to selected operations using functional updates to avoid stale state
      setSelectedOperations((prev) => {
        const updated = prev.includes("attributes-extraction")
          ? prev
          : [...prev, "attributes-extraction"];
        return updated;
      });

      setSelectedFlowConfigs((prev) => {
        const updated = prev.includes("attributes-extraction")
          ? prev
          : [...prev, "attributes-extraction"];
        return updated;
      });

      // Pre-select the specific configuration ID
      const configId = parseInt(configIdFromUrl);
      if (!isNaN(configId)) {
        setSelectedAttributesExtraction(configId);
      }
    }
  }, [configIdFromUrl, editMode]);

  // Single useEffect to handle all edit mode pre-population
  useEffect(() => {
    if (editMode && editConfigId) {
      const loadEditData = async () => {
        try {
          const originalConfig =
            await FlowConfigService.getFlowConfigurationById(
              parseInt(editConfigId)
            );

          if (!originalConfig.hasErrors && originalConfig.value) {
            const config = originalConfig.value;

            if (config.config_id) {
              setSelectedAttributesExtraction(config.config_id);
            }

            // Build operations array and set show states based on saved data
            const operations: string[] = [];

            if (config.ingestion) {
              operations.push("ingestion-agent");
              setShowIngestionFrom(true);
            }
            if (config.extraction) {
              operations.push("attributes-extraction");
              setShowAttributesExtraction(true);
            }
            if (config.tagging) {
              operations.push("document-tagging");
              setShowDocumentTagging(true);
            }
            if (config.similarity) {
              operations.push("similarity-search");
              setShowSimilaritySearch(true);
            }
            if (config.tag_configuration) {
              operations.push("tag-configurations");
              setShowTagConfigurations(true);
              setSelectedTagConfiguration(config.tag_configuration);
            }
            if (config.export) {
              operations.push("export");
              setShowExport(true);
            }

            // Set all the state variables
            setSelectedOperations(operations);
            setSelectedFlowConfigs(operations);

            // Set upload method based on the upload_method field
            if (config.upload_method) {
              setSelectedIngestionFrom(config.upload_method);
              selectedIngestionFromRef.current = config.upload_method;
            }
            if (config.sharepoint_url) {
              setSharepointUrl(config.sharepoint_url);
            }
            // Set the main dropdown to the first operation (or primary one)
            if (operations.length > 0) {
              setSelectedFlowConfig(operations[0]);
            }
          }
        } catch (error) {
          console.error("Error loading edit data:", error);
        }
      };

      loadEditData();
    }
  }, [editMode, editConfigId]);

  // All available flow configuration options
  const allFlowOptions = [
    {
      value: "ingestion-agent",
      label: "Ingestion Mode",
      required: true
    },
    {
      value: "attributes-extraction",
      label: "Attributes Extraction",
      required: true
    },
    {
      value: "tag-configurations",
      label: "Tag Configurations",
      required: true
    },
    // { value: "document-tagging", label: "Document Tagging" },
    // { value: "similarity-search", label: "Similarity Search" },
    { value: "export", label: "Export" }
  ];

  const handleToggleDocOperations = useCallback(() => {
    setIsDocOperationsOpen(!isDocOperationsOpen);
  }, [isDocOperationsOpen]);

  const handleFlowConfigChange = useCallback(
    (value: string) => {
      setSelectedFlowConfig(value);

      if (value && !selectedOperations.includes(value)) {
        setSelectedOperations([...selectedOperations, value]);
      }

      if (value && !selectedFlowConfigs.includes(value)) {
        setSelectedFlowConfigs([...selectedFlowConfigs, value]);
      }

      // Reset all conditional displays first
      setShowIngestionFrom(false);
      setShowAttributesExtraction(false);
      setShowDocumentTagging(false);
      setShowSimilaritySearch(false);
      setShowTagConfigurations(false);
      setShowExport(false);

      switch (value) {
        case "ingestion-agent":
          setShowIngestionFrom(true);
          break;
        case "attributes-extraction":
          setShowAttributesExtraction(true);
          break;
        case "document-tagging":
          setShowDocumentTagging(true);
          break;
        case "similarity-search":
          setShowSimilaritySearch(true);
          break;
        case "tag-configurations":
          setShowTagConfigurations(true);
          break;
        case "export":
          setShowExport(true);
          break;
        default:
          break;
      }
    },
    [selectedOperations, selectedFlowConfigs]
  );

  const handleAdditionalFlowConfigChange = useCallback(
    (id: number, value: string) => {
      setAdditionalFlowConfigs((prevConfigs) =>
        prevConfigs.map((config) => {
          if (config.id === id) {
            // Reset all condition
            const updatedConfig = {
              ...config,
              selectedFlow: value,
              showIngestion: false,
              showAttributes: false,
              showTagging: false,
              showSimilarity: false,
              showTagConfigurations: false,
              showExport: false
            };

            // Show appropriate conditional form based on selection
            switch (value) {
              case "ingestion-agent":
                updatedConfig.showIngestion = true;
                break;
              case "attributes-extraction":
                updatedConfig.showAttributes = true;
                break;
              case "document-tagging":
                updatedConfig.showTagging = true;
                break;
              case "similarity-search":
                updatedConfig.showSimilarity = true;
                break;
              case "tag-configurations":
                updatedConfig.showTagConfigurations = true;
                break;
              case "export":
                updatedConfig.showExport = true;
                break;
              default:
                break;
            }

            return updatedConfig;
          }
          return config;
        })
      );

      // Add to selected operations if not already present
      if (value && !selectedOperations.includes(value)) {
        setSelectedOperations([...selectedOperations, value]);
      }

      // Add to selected flow configs if not already present
      if (value && !selectedFlowConfigs.includes(value)) {
        setSelectedFlowConfigs([...selectedFlowConfigs, value]);
      }
    },
    [selectedOperations, selectedFlowConfigs]
  );

  const handleAdditionalConditionalChange = useCallback(
    (id: number, field: string, value: string) => {
      setAdditionalFlowConfigs((prevConfigs) =>
        prevConfigs.map((config) =>
          config.id === id ? { ...config, [field]: value } : config
        )
      );
    },
    []
  );

  const addNewFlowConfig = useCallback(() => {
    const newId = Date.now();
    const newFlowConfig = {
      id: newId,
      selectedFlow: "",
      showIngestion: false,
      showAttributes: false,
      showTagging: false,
      showSimilarity: false,
      showTagConfigurations: false,
      showExport: false,
      selectedIngestion: "",
      sharepointUrl: "",
      selectedAttributes: "",
      selectedTagging: "",
      selectedSimilarityValue: "",
      selectedTagConfigurationValue: "",
      selectedExportValue: ""
    };
    setAdditionalFlowConfigs((prev) => [...prev, newFlowConfig]);

    // Track the main selection
    if (
      selectedFlowConfig &&
      !selectedFlowConfigs.includes(selectedFlowConfig)
    ) {
      setSelectedFlowConfigs([...selectedFlowConfigs, selectedFlowConfig]);
    }
  }, [selectedFlowConfig, selectedFlowConfigs]);

  // Calculate dynamic height class based on number of visible additional forms
  const getConfigBoxClass = useCallback(() => {
    const mainDropdownsCount = [
      showIngestionFrom,
      showAttributesExtraction,
      showDocumentTagging,
      showSimilaritySearch,
      showTagConfigurations,
      showExport
    ].filter(Boolean).length;

    const additionalSectionsCount = additionalFlowConfigs.length;
    const additionalDropdownsCount = additionalFlowConfigs.reduce(
      (count, config) => {
        return (
          count +
          [
            config.showIngestion,
            config.showAttributes,
            config.showTagging,
            config.showSimilarity,
            config.showTagConfigurations,
            config.showExport
          ].filter(Boolean).length
        );
      },
      0
    );

    const totalActiveElements =
      mainDropdownsCount + additionalSectionsCount + additionalDropdownsCount;

    if (totalActiveElements === 0) {
      return "config-box";
    } else if (totalActiveElements <= 2) {
      return "config-box has-additional-dropdown";
    } else if (totalActiveElements <= 4) {
      return "config-box has-multiple-dropdowns";
    } else {
      return "config-box has-many-dropdowns";
    }
  }, [
    showIngestionFrom,
    showAttributesExtraction,
    showDocumentTagging,
    showSimilaritySearch,
    showTagConfigurations,
    showExport,
    additionalFlowConfigs
  ]);

  // Check if mandatory operations are selected
  const isAttributesExtractionSelected =
    selectedOperations.includes("attributes-extraction") ||
    additionalFlowConfigs.some(
      (config) => config.selectedFlow === "attributes-extraction"
    );

  const isIngestionAgentSelected =
    selectedOperations.includes("ingestion-agent") ||
    additionalFlowConfigs.some(
      (config) => config.selectedFlow === "ingestion-agent"
    );

  const handleNext = useCallback(async () => {
    // Check if user profile is loaded
    if (!userEmail) {
      ToastMessageUtil.show(
        "Please wait for user profile to load before saving.",
        ToastVariant.ERROR
      );
      return;
    }

    // Validate mandatory selections
    if (!isAttributesExtractionSelected) {
      ToastMessageUtil.show(
        "Attributes Extraction is mandatory. Please select it.",
        ToastVariant.ERROR
      );
      return;
    }

    if (!isIngestionAgentSelected) {
      ToastMessageUtil.show(
        "Ingestion Mode is mandatory. Please select it.",
        ToastVariant.ERROR
      );
      return;
    }
    // Validate SharePoint URL if SharePoint is selected
    if (selectedIngestionFrom === "sharepoint" && !sharepointUrl.trim()) {
      ToastMessageUtil.show(
        "SharePoint URL is required when SharePoint is selected.",
        ToastVariant.ERROR
      );
      return;
    }

    // Validate SharePoint URL for additional flow configs
    const additionalSharePointConfig = additionalFlowConfigs.find(
      (config) =>
        config.selectedIngestion === "sharepoint" &&
        (!config.sharepointUrl || !config.sharepointUrl.trim())
    );

    if (additionalSharePointConfig) {
      ToastMessageUtil.show(
        "SharePoint URL is required for all SharePoint configurations.",
        ToastVariant.ERROR
      );
      return;
    }

    setIsSaving(true);
    setSaveError(null);

    try {
      // Determine config_id based on document type
      const documentType = documentTypeFromUrl
        ? decodeURIComponent(documentTypeFromUrl).toLowerCase()
        : "contract";

      // Both engineering and contract documents use the selected attributes extraction config_id from the form
      const selectedConfigId =
        selectedAttributesExtraction ||
        additionalFlowConfigs.find(
          (config) => config.selectedFlow === "attributes-extraction"
        )?.selectedAttributes;

      if (!selectedConfigId) {
        throw new Error("Please select an extraction configuration");
      }

      // TODO: Replace with actual user authentication context
      // For now, using a placeholder - this should come from your auth context/service
      const currentUserForAPI = currentUser; // Using the actual logged-in user

      // Capture the current upload_method using ref to avoid stale closure
      const currentUploadMethod =
        selectedIngestionFromRef.current === "blob" ||
        additionalFlowConfigs.some(
          (config) => config.selectedIngestion === "blob"
        )
          ? "blob"
          : selectedIngestionFromRef.current === "sharepoint" ||
            additionalFlowConfigs.some(
              (config) => config.selectedIngestion === "sharepoint"
            )
          ? "sharepoint"
          : "manual";
      // Build the payload based on selected operations
      const flowConfigData: CreateFlowConfigRequest = {
        config_id: Number(selectedConfigId),
        created_by: userEmail,
        created_by_email: userEmail,
        flow_name: flowName,
        flow_description: flowDescription,
        upload_method: currentUploadMethod,
        ...(currentUploadMethod === "sharepoint" && {
          sharepoint_url: sharepointUrl
        }),
        document_type: documentTypeFromUrl
          ? decodeURIComponent(documentTypeFromUrl)
          : "Others", // Decode document type from URL or default to Others
        ingestion:
          selectedOperations.includes("ingestion-agent") ||
          additionalFlowConfigs.some(
            (config) => config.selectedFlow === "ingestion-agent"
          ),
        extraction:
          selectedOperations.includes("attributes-extraction") ||
          additionalFlowConfigs.some(
            (config) => config.selectedFlow === "attributes-extraction"
          ),
        tagging:
          selectedOperations.includes("document-tagging") ||
          additionalFlowConfigs.some(
            (config) => config.selectedFlow === "document-tagging"
          ),
        similarity:
          selectedOperations.includes("similarity-search") ||
          additionalFlowConfigs.some(
            (config) => config.selectedFlow === "similarity-search"
          ),
        export:
          selectedOperations.includes("export") ||
          additionalFlowConfigs.some(
            (config) => config.selectedFlow === "export"
          ),
        ...(selectedTagConfiguration && {
          tag_configuration: selectedTagConfiguration
        }),
        created_time: new Date().toISOString()
      };

      let result;
      if (editMode && editConfigId) {
        // Fetch original config to preserve created_time and get id
        const originalConfig = await FlowConfigService.getFlowConfigurationById(
          parseInt(editConfigId!)
        );

        if (!originalConfig.hasErrors && originalConfig.value) {
          // Update with original created_time and upload_method, matching expected API structure
          const updateData = {
            config_id: flowConfigData.config_id,
            created_by: userEmail,
            created_by_email: userEmail,
            flow_name: flowConfigData.flow_name,
            flow_description: flowConfigData.flow_description,
            upload_method: flowConfigData.upload_method, // Include upload_method in update
            ...(flowConfigData.upload_method === "sharepoint" && {
              sharepoint_url: flowConfigData.sharepoint_url
            }),
            document_type: documentTypeFromUrl
              ? decodeURIComponent(documentTypeFromUrl)
              : "Others", // Decode document type
            ingestion: flowConfigData.ingestion,
            extraction: flowConfigData.extraction,
            tagging: flowConfigData.tagging,
            similarity: flowConfigData.similarity,
            export: flowConfigData.export,
            ...(selectedTagConfiguration && {
              tag_configuration: selectedTagConfiguration
            }),
            created_time: originalConfig.value.created_time
          };

          result = await FlowConfigService.updateFlowConfiguration(
            parseInt(editConfigId!),
            updateData
          );
        } else {
          throw new Error("Failed to fetch original configuration data");
        }
      } else {
        // Create new configuration
        const headers = { "X-User-ID": userEmail, "X-User-Email": userEmail };
        result = await FlowConfigService.createFlowConfiguration(
          flowConfigData,
          headers
        );
      }

      if (!result.hasErrors && result.value) {
        // Show success message
        ToastMessageUtil.show(
          editMode
            ? "Configuration updated successfully"
            : "Configuration created successfully",
          ToastVariant.SUCCESS
        );

        // Navigate back to assignment page
        navigate("/assignment");
      } else {
        const errorMessage =
          result.errors.length > 0
            ? result.errors.join(", ")
            : "Failed to save flow configuration";
        setSaveError(errorMessage);
      }
    } catch (error) {
      const errorMessage =
        error instanceof Error ? error.message : "Unknown error occurred";
      setSaveError(errorMessage);
      console.error("Error saving flow configuration:", error);
    } finally {
      setIsSaving(false);
    }
  }, [
    isAttributesExtractionSelected,
    selectedAttributesExtraction,
    selectedOperations,
    additionalFlowConfigs,
    flowName,
    flowDescription,
    currentUser,
    userEmail,
    navigate,
    editMode,
    editConfigId,
    documentTypeFromUrl
  ]);

  if (isLoadingConfigs) {
    return (
      <>
        <HeaderNew showSearch={false} />
        <div className="configure-ops-container">
          <HubHeader
            className="header"
            showBackButton={true}
            backRoute="/configure-ops"
          />
          <div style={{ padding: "20px", textAlign: "center" }}>
            Loading configuration operations...
          </div>
        </div>
      </>
    );
  }

  return (
    <>
      <HeaderNew showSearch={false} />
      <div className="configure-ops-container">
        <HubHeader
          className="header"
          showBackButton={true}
          backRoute="/configure-ops"
        />

        {/* Main Config Box */}
        <div className={getConfigBoxClass()}>
          <div className="main-area">
            {/* Sidebar Component */}
            <SidebarConfigureOps
              isDocOperationsOpen={isDocOperationsOpen}
              selectedOperations={selectedOperations}
              onToggleDocOperations={handleToggleDocOperations}
              currentStep={currentStep}
              operations={steps}
            />

            {/* Form Area */}
            <div className="form-area">
              <div className="document-operations-section">
                <h3>
                  Document Operations{" "}
                  <span className="required-text">(required)</span>
                </h3>

                <div className="info-text">
                  <InfoIcon fontSize="small" color="primary" />
                  Begin flow configuration by selecting from the below dropdown,
                  click on Add more to keep adding sections.
                </div>

                <div className="form-group">
                  <label>Select step</label>
                  <div className="select-container">
                    <select
                      value={selectedFlowConfig}
                      onChange={(e) => handleFlowConfigChange(e.target.value)}
                      className="form-select"
                    >
                      <option value="" disabled>
                        Select flow
                      </option>
                      {allFlowOptions.map((option) => (
                        <option
                          key={option.value}
                          value={option.value}
                          data-required={option.required || false}
                        >
                          {option.label}
                          {option.required && " *"}
                        </option>
                      ))}
                    </select>
                    <button className="add-more-btn" onClick={addNewFlowConfig}>
                      <svg
                        fill="none"
                        stroke="currentColor"
                        viewBox="0 0 24 24"
                        style={{ width: "0.875rem", height: "0.875rem" }}
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth={2}
                          d="M12 4v16m8-8H4"
                        />
                      </svg>
                      Add more
                    </button>
                  </div>
                </div>

                {showIngestionFrom && (
                  <div className="form-group ingestion-form-group">
                    <label>
                      Ingestion From{" "}
                      <span className="required-text">(required)</span>
                    </label>
                    <div className="select-container">
                      <select
                        value={selectedIngestionFrom}
                        onChange={(e) => {
                          setSelectedIngestionFrom(e.target.value);
                          selectedIngestionFromRef.current = e.target.value;
                        }}
                        className="form-select"
                      >
                        <option value="" disabled>
                          Upload File
                        </option>
                        <option value="local">Upload from Local</option>
                        <option value="blob">Upload from Blob</option>
                        <option value="sharepoint">
                          Upload from SharePoint
                        </option>
                      </select>
                    </div>
                  </div>
                )}
                {showIngestionFrom &&
                  selectedIngestionFrom === "sharepoint" && (
                    <div className="form-group ingestion-form-group">
                      <label>
                        SharePoint URL{" "}
                        <span className="required-text">(required)</span>
                      </label>
                      <input
                        type="text"
                        value={sharepointUrl}
                        onChange={(e) => setSharepointUrl(e.target.value)}
                        className="form-input"
                        placeholder="Enter SharePoint URL"
                      />
                    </div>
                  )}

                {showAttributesExtraction && (
                  <div className="form-group ingestion-form-group">
                    <label>
                      Extraction Configuration{" "}
                      <span className="required-text">(required)</span>
                    </label>
                    <div className="select-container">
                      <select
                        value={selectedAttributesExtraction ?? ""}
                        onChange={(e) => {
                          const value =
                            e.target.value === ""
                              ? null
                              : Number(e.target.value);
                          setSelectedAttributesExtraction(value);
                        }}
                        className="form-select"
                        disabled={isLoadingOptions}
                      >
                        <option value="" disabled>
                          {isLoadingOptions
                            ? "Loading..."
                            : "Select configuration"}
                        </option>
                        {attributeExtractionOptions
                          .filter(
                            (option) =>
                              option.doc_type_id.toString() ===
                              selectedDocumentType.toString()
                          )
                          .map((option) => (
                            <option
                              key={option.config_id}
                              value={Number(option.config_id)}
                            >
                              {option.config_name}
                            </option>
                          ))}
                      </select>
                      {optionsError && (
                        <div
                          className="error-text"
                          style={{
                            color: "#dc2626",
                            fontSize: "0.875rem",
                            marginTop: "4px"
                          }}
                        >
                          Error loading options: {optionsError}
                        </div>
                      )}
                    </div>
                  </div>
                )}

                {showTagConfigurations && (
                  <div className="form-group ingestion-form-group">
                    <label>
                      Tag Configuration{" "}
                      <span className="required-text">(required)</span>
                    </label>
                    <div className="select-container">
                      <select
                        value={selectedTagConfiguration}
                        onChange={(e) =>
                          setSelectedTagConfiguration(e.target.value)
                        }
                        className="form-select"
                        disabled={isLoadingTagOptions}
                      >
                        <option value="" disabled>
                          {isLoadingTagOptions
                            ? "Loading..."
                            : "Select tag configuration"}
                        </option>
                        {Array.isArray(tagConfigurationOptions) &&
                          tagConfigurationOptions.map((option) => (
                            <option
                              key={option.id || option.name}
                              value={option.name}
                            >
                              {option.name}
                            </option>
                          ))}
                      </select>
                      {tagOptionsError && (
                        <div
                          className="error-text"
                          style={{
                            color: "#dc2626",
                            fontSize: "0.875rem",
                            marginTop: "4px"
                          }}
                        >
                          Error loading tag configurations: {tagOptionsError}
                        </div>
                      )}
                    </div>
                  </div>
                )}

                {showDocumentTagging && (
                  <div className="document-tagging-interface">
                    <div className="tagging-header">
                      <div className="tagging-buttons">
                        <button className="associate-tags-btn">
                          Associate Tags
                        </button>
                        <button className="create-new-tag-btn">
                          Create New Tag
                        </button>
                      </div>
                    </div>

                    <div className="tagging-table">
                      <div className="table-header">
                        <div className="header-cell">Tag Category</div>
                        <div className="header-cell">Description</div>
                        <div className="header-cell">Tags</div>
                      </div>

                      <div className="table-row">
                        <div className="table-cell">
                          <span className="category-name">Equipment Class</span>
                        </div>
                        <div className="table-cell">
                          <span className="description-text">
                            Equipment class is a grouping of similar equipment
                            items sharing design and operational characteristics
                          </span>
                        </div>
                        <div className="table-cell">
                          <div className="tags-section">
                            <div className="tag-item">
                              <span className="tag-text">
                                Fixed Equipment Class...
                              </span>
                              <button className="remove-tag-btn">×</button>
                            </div>
                          </div>
                        </div>
                      </div>

                      <div className="table-row table-row-with-separator">
                        <div className="table-cell">
                          <span className="category-name">
                            Discipline Class
                          </span>
                        </div>
                        <div className="table-cell">
                          <span className="description-text">
                            Discipline class is a grouping of similar equipment
                            items sharing design and operational characteristics
                          </span>
                        </div>
                        <div className="table-cell">
                          <div className="tags-section">
                            <div className="tag-item">
                              <span className="tag-text">
                                Fixed Discipline Class...
                              </span>
                              <button className="remove-tag-btn">×</button>
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>

                    <div className="add-tag-section">
                      <button className="add-tag-btn">
                        <span>+</span> Add Tag
                      </button>
                    </div>
                  </div>
                )}

                {showExport && (
                  <div className="form-group ingestion-form-group">
                    <label>
                      Export As{" "}
                      <span className="required-text">(required)</span>
                    </label>
                    <div className="select-container">
                      <select
                        value={selectedExport}
                        onChange={(e) => setSelectedExport(e.target.value)}
                        className="form-select"
                      >
                        <option value="" disabled>
                          Select format
                        </option>
                        <option value="excel">Excel</option>
                        <option value="Word">Word</option>
                      </select>
                    </div>
                  </div>
                )}

                {/* Additional Flow Configuration Sections */}
                {additionalFlowConfigs.map((flowConfig) => (
                  <div key={flowConfig.id} className="additional-flow-section">
                    <div className="form-group">
                      <label>Select flow configuration</label>
                      <div className="select-container">
                        <select
                          value={flowConfig.selectedFlow}
                          onChange={(e) =>
                            handleAdditionalFlowConfigChange(
                              flowConfig.id,
                              e.target.value
                            )
                          }
                          className="form-select"
                        >
                          <option value="" disabled>
                            Select flow
                          </option>
                          {allFlowOptions
                            .filter(
                              (option) =>
                                option.value !== selectedFlowConfig &&
                                !additionalFlowConfigs.some(
                                  (config) =>
                                    config.id !== flowConfig.id &&
                                    config.selectedFlow === option.value
                                )
                            )
                            .map((option) => (
                              <option
                                key={option.value}
                                value={option.value}
                                data-required={option.required || false}
                              >
                                {option.label}
                                {option.required && " *"}
                              </option>
                            ))}
                        </select>
                      </div>
                    </div>

                    {/* Conditional forms for this specific flow config */}
                    {flowConfig.showIngestion && (
                      <div className="form-group ingestion-form-group">
                        <label>
                          Ingestion From{" "}
                          <span className="required-text">(required)</span>
                        </label>
                        <div className="select-container">
                          <select
                            value={flowConfig.selectedIngestion}
                            onChange={(e) =>
                              handleAdditionalConditionalChange(
                                flowConfig.id,
                                "selectedIngestion",
                                e.target.value
                              )
                            }
                            className="form-select"
                          >
                            <option value="" disabled>
                              Upload File
                            </option>
                            <option value="local">Upload from local</option>
                            <option value="blob">Upload from Blob</option>
                            <option value="sharepoint">
                              Upload from SharePoint
                            </option>
                          </select>
                        </div>
                      </div>
                    )}
                    {flowConfig.showIngestion &&
                      flowConfig.selectedIngestion === "sharepoint" && (
                        <div className="form-group ingestion-form-group">
                          <label>
                            SharePoint URL{" "}
                            <span className="required-text">(required)</span>
                          </label>
                          <input
                            type="text"
                            value={flowConfig.sharepointUrl || ""}
                            onChange={(e) =>
                              handleAdditionalConditionalChange(
                                flowConfig.id,
                                "sharepointUrl",
                                e.target.value
                              )
                            }
                            className="form-input"
                            placeholder="Enter SharePoint URL"
                          />
                        </div>
                      )}

                    {flowConfig.showAttributes && (
                      <div className="form-group ingestion-form-group">
                        <label>
                          Extraction Configuration{" "}
                          <span className="required-text">(required)</span>
                        </label>
                        <div className="select-container">
                          <select
                            value={flowConfig.selectedAttributes}
                            onChange={(e) =>
                              handleAdditionalConditionalChange(
                                flowConfig.id,
                                "selectedAttributes",
                                e.target.value
                              )
                            }
                            className="form-select"
                            disabled={isLoadingOptions}
                          >
                            <option value="" disabled>
                              {isLoadingOptions
                                ? "Loading..."
                                : "Select configuration"}
                            </option>
                            {attributeExtractionOptions.map((option) => (
                              <option
                                key={option.config_id}
                                value={option.config_id.toString()}
                              >
                                {option.config_name}
                              </option>
                            ))}
                          </select>
                          {optionsError && (
                            <div
                              className="error-text"
                              style={{
                                color: "#dc2626",
                                fontSize: "0.875rem",
                                marginTop: "4px"
                              }}
                            >
                              Error loading options: {optionsError}
                            </div>
                          )}
                        </div>
                      </div>
                    )}

                    {flowConfig.showTagging && (
                      <div className="document-tagging-interface">
                        <div className="tagging-header">
                          <div className="tagging-buttons">
                            <button className="associate-tags-btn">
                              Associate Tags
                            </button>
                            <button className="create-new-tag-btn">
                              Create New Tag
                            </button>
                          </div>
                        </div>

                        <div className="tagging-table">
                          <div className="table-header">
                            <div className="header-cell">Tag Category</div>
                            <div className="header-cell">Description</div>
                            <div className="header-cell">Tags</div>
                          </div>

                          <div className="table-row">
                            <div className="table-cell">
                              <span className="category-name">
                                Equipment Class
                              </span>
                            </div>
                            <div className="table-cell">
                              <span className="description-text">
                                Equipment class is a grouping of similar
                                equipment items sharing design and operational
                                characteristics
                              </span>
                            </div>
                            <div className="table-cell">
                              <div className="tags-section">
                                <div className="tag-item">
                                  <span className="tag-text">
                                    Fixed Equipment Class...
                                  </span>
                                  <button className="remove-tag-btn">×</button>
                                </div>
                              </div>
                            </div>
                          </div>

                          <div className="table-row table-row-with-separator">
                            <div className="table-cell">
                              <span className="category-name">
                                Discipline Class
                              </span>
                            </div>
                            <div className="table-cell">
                              <span className="description-text">
                                Discipline class is a grouping of similar
                                equipment items sharing design and operational
                                characteristics
                              </span>
                            </div>
                            <div className="table-cell">
                              <div className="tags-section">
                                <div className="tag-item">
                                  <span className="tag-text">
                                    Fixed Discipline Class...
                                  </span>
                                  <button className="remove-tag-btn">×</button>
                                </div>
                              </div>
                            </div>
                          </div>
                        </div>

                        <div className="add-tag-section">
                          <button className="add-tag-btn">
                            <span>+</span> Add Tag
                          </button>
                        </div>
                      </div>
                    )}

                    {flowConfig.showTagConfigurations && (
                      <div className="form-group ingestion-form-group">
                        <label>
                          Tag Configuration{" "}
                          <span className="required-text">(required)</span>
                        </label>
                        <div className="select-container">
                          <select
                            value={flowConfig.selectedTagConfigurationValue}
                            onChange={(e) =>
                              handleAdditionalConditionalChange(
                                flowConfig.id,
                                "selectedTagConfigurationValue",
                                e.target.value
                              )
                            }
                            className="form-select"
                            disabled={isLoadingTagOptions}
                          >
                            <option value="" disabled>
                              {isLoadingTagOptions
                                ? "Loading..."
                                : "Select tag configuration"}
                            </option>
                            {Array.isArray(tagConfigurationOptions) &&
                              tagConfigurationOptions.map((option) => (
                                <option
                                  key={option.id || option.name}
                                  value={option.name}
                                >
                                  {option.name}
                                </option>
                              ))}
                          </select>
                          {tagOptionsError && (
                            <div
                              className="error-text"
                              style={{
                                color: "#dc2626",
                                fontSize: "0.875rem",
                                marginTop: "4px"
                              }}
                            >
                              Error loading tag configurations:{" "}
                              {tagOptionsError}
                            </div>
                          )}
                        </div>
                      </div>
                    )}

                    {flowConfig.showExport && (
                      <div className="form-group ingestion-form-group">
                        <label>
                          Export As{" "}
                          <span className="required-text">(required)</span>
                        </label>
                        <div className="select-container">
                          <select
                            value={flowConfig.selectedExportValue}
                            onChange={(e) =>
                              handleAdditionalConditionalChange(
                                flowConfig.id,
                                "selectedExportValue",
                                e.target.value
                              )
                            }
                            className="form-select"
                          >
                            <option value="" disabled>
                              Select format
                            </option>
                            <option value="excel">Excel</option>
                            <option value="Word">Word</option>
                          </select>
                        </div>
                      </div>
                    )}
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>

        {/* Error Display */}
        {saveError && (
          <div
            className="error-message"
            style={{
              background: "#fee2e2",
              border: "1px solid #fecaca",
              color: "#dc2626",
              padding: "12px",
              margin: "0 24px 16px 24px",
              borderRadius: "6px",
              fontSize: "14px"
            }}
          >
            <strong>Error:</strong> {saveError}
          </div>
        )}

        {/* Main Action Buttons */}
        <div className="main-action-buttons">
          <button
            className="cancel-btn"
            onClick={() => navigate("/assignment")}
          >
            Cancel
          </button>
          <button
            className={`next-btn ${
              !isAttributesExtractionSelected || isSaving || !userEmail
                ? "disabled"
                : ""
            }`}
            onClick={handleNext}
            disabled={!isAttributesExtractionSelected || isSaving || !userEmail}
          >
            {isSaving ? "Saving..." : "Save"}
          </button>
        </div>
      </div>
    </>
  );
};

export default DocumentOperation;
